/**
 * Pure screen-layout helpers for the map overlay: fanning out pins that sit on top of each other,
 * placing labels without collisions, placing a line's label, the "context" zoom cap and colour contrast.
 * No DOM or canvas here, so everything is unit-tested (tests/unit/map-layout.test.ts).
 */

/** Axis-aligned box [x0, y0, x1, y1] in screen px. */
export type Box = [number, number, number, number];

export interface Dot {
  x: number;
  y: number;
  /** Radius of the drawn pin. */
  r: number;
  /** For a pin fanned out of a cluster: the direction it was pushed (label spots on that side come first). */
  dir?: { x: number; y: number } | null;
}

export interface Placed {
  x: number;
  y: number;
  /** True when the pin was pushed away from its real spot (draw a leader line). */
  moved: boolean;
}

/** Pins closer than this (centre to centre, px) are fanned out. */
export const FAN_MIN = 22;
const FAN_GAP = 4;

const need = (a: Dot, b: Dot, minDist: number) => Math.max(minDist, a.r + b.r + FAN_GAP);

/**
 * Spread pins whose screen positions are closer than `minDist` around their centroid, on a small circle,
 * keeping their circular order, so every pin stays visible and tappable. Isolated pins are returned unchanged.
 * Clusters whose circles would touch each other are merged and laid out again.
 */
export function fanOut(pts: readonly Dot[], minDist = FAN_MIN): Placed[] {
  const n = pts.length;
  const out: Placed[] = pts.map((p) => ({ x: p.x, y: p.y, moved: false }));
  if (n < 2) return out;
  // union-find on the real positions
  const parent = pts.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i]!)));
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb);
  };
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) if (Math.hypot(pts[i]!.x - pts[j]!.x, pts[i]!.y - pts[j]!.y) < need(pts[i]!, pts[j]!, minDist)) union(i, j);

  for (let pass = 0; pass < 6; pass++) {
    const groups = new Map<number, number[]>();
    for (let i = 0; i < n; i++) {
      const r = find(i);
      groups.set(r, [...(groups.get(r) ?? []), i]);
    }
    for (const members of groups.values()) layoutRing(pts, members, out, minDist);
    // merge clusters whose laid-out pins still collide
    let merged = false;
    for (let i = 0; i < n; i++)
      for (let j = i + 1; j < n; j++) {
        if (find(i) === find(j)) continue;
        if (Math.hypot(out[i]!.x - out[j]!.x, out[i]!.y - out[j]!.y) < need(pts[i]!, pts[j]!, minDist)) {
          union(i, j);
          merged = true;
        }
      }
    if (!merged) break;
  }
  return out;
}

function layoutRing(pts: readonly Dot[], members: number[], out: Placed[], minDist: number): void {
  const m = members.length;
  if (m === 1) {
    const i = members[0]!;
    out[i] = { x: pts[i]!.x, y: pts[i]!.y, moved: false };
    return;
  }
  let cx = 0;
  let cy = 0;
  let maxR = 0;
  for (const i of members) {
    cx += pts[i]!.x / m;
    cy += pts[i]!.y / m;
    maxR = Math.max(maxR, pts[i]!.r);
  }
  const spacing = Math.max(minDist, 2 * maxR + FAN_GAP);
  const R = spacing / (2 * Math.sin(Math.PI / m));
  const spread = Math.max(...members.map((i) => Math.hypot(pts[i]!.x - cx, pts[i]!.y - cy)));
  const ang = (i: number) => Math.atan2(pts[i]!.y - cy, pts[i]!.x - cx);
  // keep the pins' circular order; identical spots keep their input order
  const order = spread < 0.5 ? [...members] : [...members].sort((a, b) => ang(a) - ang(b) || a - b);
  const step = (2 * Math.PI) / m;
  let base: number;
  if (spread < 0.5) base = m === 2 ? Math.PI : -Math.PI / 2; // two on one spot: left and right; more: start at the top
  else {
    // rotation that moves the pins least: circular mean of (angle − slot angle)
    let sx = 0;
    let sy = 0;
    order.forEach((i, s) => {
      const a = ang(i) - s * step;
      sx += Math.cos(a);
      sy += Math.sin(a);
    });
    base = Math.atan2(sy, sx);
  }
  order.forEach((i, s) => {
    const a = base + s * step;
    out[i] = { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a), moved: true };
  });
}

export const overlapArea = (a: Box, b: Box): number =>
  Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0])) * Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));

const totalOverlap = (b: Box, occ: readonly Box[]) => occ.reduce((s, o) => s + overlapArea(b, o), 0);

/** Shift a box inside the bounds; returns the shifted box and how far it moved. */
function clampBox(b: Box, bounds: Box): { box: Box; shift: number } {
  const w = b[2] - b[0];
  const h = b[3] - b[1];
  const x0 = Math.max(bounds[0], Math.min(bounds[2] - w, b[0]));
  const y0 = Math.max(bounds[1], Math.min(bounds[3] - h, b[1]));
  return { box: [x0, y0, x0 + w, y0 + h], shift: Math.hypot(x0 - b[0], y0 - b[1]) };
}

export interface LabelSpot {
  /** Left edge of the text. */
  x: number;
  /** Vertical middle of the text block. */
  y: number;
  box: Box;
}

/** Distance from a point to the nearest point of a box (0 inside it). */
export function distToBox(x: number, y: number, b: Box): number {
  return Math.hypot(Math.max(b[0] - x, 0, x - b[2]), Math.max(b[1] - y, 0, y - b[3]));
}

/** The point of a box nearest to (x, y): where a leader line from a pin meets its label. */
export const nearestOnBox = (x: number, y: number, b: Box): [number, number] => [Math.max(b[0], Math.min(b[2], x)), Math.max(b[1], Math.min(b[3], y))];

/** A label further than this from the edge of its pin gets a leader line back to the pin. */
export const LEADER_GAP = 11;

/** How far out the rings of label spots go: next to the pin, then further out (with a leader line) in crowded places. */
const RINGS = [0, 14, 34, 60];

/**
 * Label spots around a pin at (x, y) with radius r, in order of preference: right, left, above, below and the four
 * diagonals, then the same on rings further out. Spots that don't fit are pulled inside `bounds`, but only as far
 * as keeps them next to their pin (never over it, never nearer another place than their own); `shift` says how far.
 */
function labelSpots(pin: Dot, w: number, h: number, bounds: Box): (LabelSpot & { shift: number; ring: number })[] {
  const { x, y, r } = pin;
  const out: (LabelSpot & { shift: number; ring: number })[] = [];
  RINGS.forEach((extra, ring) => {
    const g = 7 + extra;
    const d = r * 0.72 + 6 + extra; // diagonal offset
    const up = y - r - h / 2 - 4 - extra;
    const down = y + r + h / 2 + 4 + extra;
    const cands: [number, number][] = [
      [x + r + g, y],
      [x - r - g - w, y],
      [x - w / 2, up],
      [x - w / 2, down],
      [x + d, y - d - h / 2],
      [x + d, y + d + h / 2],
      [x - d - w, y - d - h / 2],
      [x - d - w, y + d + h / 2],
      // above/below, running right or left from the pin
      [x - r, up],
      [x - r, down],
      [x + r - w, up],
      [x + r - w, down],
    ];
    const far = r + 12 + extra * 1.25;
    for (const [cx, cy] of cands) {
      const { box, shift } = clampBox([cx - 2, cy - h / 2, cx + w + 2, cy + h / 2], bounds);
      const dist = distToBox(x, y, box);
      if (dist < r + 1 || dist > far) continue;
      out.push({ x: box[0] + 2, y: (box[1] + box[3]) / 2, box, shift, ring });
    }
  });
  // nearest ring first; within a ring, unshifted spots before shifted ones, keeping the preference order — or, for a
  // pin fanned out of a cluster, the spots on the outer side first, so each label sits clear of its neighbours' pins
  const dir = pin.dir;
  const away = (o: LabelSpot): number => {
    if (!dir) return 0;
    const cx = (o.box[0] + o.box[2]) / 2 - x;
    const cy = (o.box[1] + o.box[3]) / 2 - y;
    return -(cx * dir.x + cy * dir.y) / (Math.hypot(cx, cy) || 1);
  };
  return out
    .map((o, i) => ({ o, i, a: away(o) }))
    .sort((a, b) => a.o.ring - b.o.ring || Number(a.o.shift > 0) - Number(b.o.shift > 0) || a.a - b.a || a.i - b.i)
    .map((e) => e.o);
}

const forcedSpot = (pin: Dot, w: number, h: number, occ: readonly Box[], bounds: Box): LabelSpot => {
  let best: { spot: LabelSpot; cost: number } | null = null;
  for (const s of labelSpots(pin, w, h, bounds)) {
    const cost = totalOverlap(s.box, occ) * 4 + s.shift + s.ring * 6;
    if (!best || cost < best.cost) best = { spot: { x: s.x, y: s.y, box: s.box }, cost };
  }
  if (best) return best.spot;
  const { box } = clampBox([pin.x + pin.r + 5, pin.y - h / 2, pin.x + pin.r + 9 + w, pin.y + h / 2], bounds);
  return { x: box[0] + 2, y: (box[1] + box[3]) / 2, box };
};

/**
 * Place a label of size w × h next to a pin. A spot must stay inside `bounds` and must not overlap `occ`. `soft`
 * boxes (e.g. route lines) are avoided when there's a free spot on the first three rings, else accepted. When nothing is free, a forced label takes the spot with the least overlap; an unforced label returns null.
 */
export function placeLabel(pin: Dot, w: number, h: number, occ: readonly Box[], bounds: Box, force = false, soft: readonly Box[] = []): LabelSpot | null {
  const spots = labelSpots(pin, w, h, bounds).filter((s) => totalOverlap(s.box, occ) === 0);
  const clean = spots.filter((s) => !soft.length || totalOverlap(s.box, soft) === 0);
  // a label keeps off route lines if it can stay fairly close; further out it just takes the nearest free spot
  const s = clean.find((c) => c.ring <= 2) ?? spots[0];
  if (s) return { x: s.x, y: s.y, box: s.box };
  return force ? forcedSpot(pin, w, h, occ, bounds) : null;
}

export interface LabelReq extends Dot {
  w: number;
  h: number;
  /** Higher is more important. */
  rank: number;
  force?: boolean;
}

/**
 * Place many pin labels. More important labels go first; one that finds no free spot may take a spot held only by
 * less important labels, which then look for another spot (or are dropped). `occ` holds pins and other fixed
 * obstacles; label boxes never overlap it unless the label is forced. `soft` (route lines) is avoided where possible.
 */
export function placeLabels(reqs: readonly LabelReq[], occ: readonly Box[], bounds: Box, soft: readonly Box[] = []): (LabelSpot | null)[] {
  const out: (LabelSpot | null)[] = reqs.map(() => null);
  const evictions = reqs.map(() => 0);
  const byRank = (a: number, b: number) => reqs[b]!.rank - reqs[a]!.rank || a - b;
  const queue = reqs.map((_, i) => i).sort(byRank);
  const placedBoxes = (except = -1) => out.flatMap((s, j) => (s && j !== except ? [s.box] : []));
  while (queue.length) {
    const i = queue.shift()!;
    const q = reqs[i]!;
    const all = [...occ, ...placedBoxes()];
    const free = placeLabel(q, q.w, q.h, all, bounds, false, soft);
    if (free) {
      out[i] = free;
      continue;
    }
    // take a spot blocked only by lower-ranked labels (each label is pushed out at most twice, so this ends)
    let taken = false;
    for (const s of labelSpots(q, q.w, q.h, bounds)) {
      if (totalOverlap(s.box, occ) > 0) continue;
      const blockers = out.flatMap((o, j) => (o && overlapArea(o.box, s.box) > 0 ? [j] : []));
      if (blockers.some((j) => reqs[j]!.rank >= q.rank || reqs[j]!.force || evictions[j]! >= 2)) continue;
      for (const j of blockers) {
        out[j] = null;
        evictions[j]!++;
        queue.push(j);
      }
      queue.sort(byRank);
      out[i] = { x: s.x, y: s.y, box: s.box };
      taken = true;
      break;
    }
    if (!taken && q.force) out[i] = forcedSpot(q, q.w, q.h, all, bounds);
  }
  return out;
}

/**
 * Centre of a line's label (e.g. the verdict distance): near the middle of the segment a→b, pushed off the line
 * sideways so it covers neither end's pin nor anything in `occ`; prefers the side above the line.
 */
export function placeLineLabel(a: { x: number; y: number }, b: { x: number; y: number }, w: number, h: number, occ: readonly Box[], bounds: Box): { x: number; y: number; box: Box } {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  let nx = 0;
  let ny = -1;
  if (len > 1e-6) {
    nx = -(b.y - a.y) / len;
    ny = (b.x - a.x) / len;
    if (ny > 0 || (ny === 0 && nx > 0)) {
      nx = -nx;
      ny = -ny;
    }
  }
  // distance from the line to the box edge along the normal, so the box clears the line itself
  const half = Math.abs(nx) * (w / 2) + Math.abs(ny) * (h / 2);
  const tries: [number, number][] = [];
  for (const off of [6, 16, 30, 48]) for (const side of [1, -1]) tries.push([mx + nx * (half + off) * side, my + ny * (half + off) * side]);
  const boxAt = (cx: number, cy: number): Box => [cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2];
  let best: { x: number; y: number; box: Box; cost: number } | null = null;
  for (const [cx, cy] of tries) {
    const { box, shift } = clampBox(boxAt(cx, cy), bounds);
    const cost = totalOverlap(box, occ) * 4 + shift;
    const spot = { x: (box[0] + box[2]) / 2, y: (box[1] + box[3]) / 2, box };
    if (cost === 0) return spot;
    if (!best || cost < best.cost) best = { ...spot, cost };
  }
  return { x: best!.x, y: best!.y, box: best!.box };
}

/**
 * Small boxes along a drawn line (screen px), every `step` px, for labels to avoid: a label across a route line
 * looks struck through. Only the part inside `bounds` counts.
 */
export function lineBoxes(pts: readonly { x: number; y: number }[], bounds: Box, step = 8, half = 3): Box[] {
  const out: Box[] = [];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / step));
    for (let j = 0; j <= n; j++) {
      const x = a.x + ((b.x - a.x) * j) / n;
      const y = a.y + ((b.y - a.y) * j) / n;
      if (x < bounds[0] - half || x > bounds[2] + half || y < bounds[1] - half || y > bounds[3] + half) continue;
      out.push([x - half, y - half, x + half, y + half]);
    }
  }
  return out;
}

/** Earth's equatorial circumference in km: a world `wpx` px wide covers this at the equator. */
export const EARTH_KM = 40075;

/**
 * World width in px at which the shorter side of a w × h view spans about `spanKm` (more on big screens, at most
 * `minKmPerPx` km per px) at latitude `lat`.
 * Used to cap how close a fit zooms on a single place, so the view keeps a few hundred km of context.
 */
export function contextWorldPx(w: number, h: number, lat: number, spanKm = 300, minKmPerPx = 0.9): number {
  const cos = Math.max(0.05, Math.cos((lat * Math.PI) / 180));
  const side = Math.min(w, h);
  // never closer than ~1 km per px: the 1:10m coastline turns blocky beyond that
  return (side * EARTH_KM * cos) / Math.max(spanKm, side * minKmPerPx);
}

export interface Pad {
  l: number;
  r: number;
  t: number;
  b: number;
}

/**
 * Fit padding: the zoom buttons sit in the top-right corner (34 px wide + 12 px margin), and labels are drawn to
 * the right of pins first, so the right side gets the most room.
 */
export function fitPadding(w: number): Pad {
  return w < 760 ? { l: 40, r: 72, t: 36, b: 50 } : { l: 70, r: 110, t: 60, b: 70 };
}

// ---------- colour ----------
type RGB = [number, number, number];

/** Parse #rgb, #rrggbb, rgb() or rgba() (alpha ignored). */
export function parseColor(s: string): RGB | null {
  const c = s.trim();
  let m = /^#([0-9a-f]{3})$/i.exec(c);
  if (m) return [...m[1]!].map((d) => parseInt(d + d, 16)) as RGB;
  m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(c);
  if (m) return [0, 2, 4].map((i) => parseInt(m![1]!.slice(i, i + 2), 16)) as RGB;
  m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(c);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return null;
}

const lin = (v: number) => {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};
export const luminance = ([r, g, b]: RGB): number => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);

/** WCAG contrast ratio of two colours (1 when either can't be parsed). */
export function contrast(a: string, b: string): number {
  const x = parseColor(a);
  const y = parseColor(b);
  if (!x || !y) return 1;
  const [hi, lo] = [luminance(x), luminance(y)].sort((p, q) => q - p) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** The candidate with the best contrast against `bg`. */
export const bestOn = (bg: string, cands: readonly string[]): string => cands.reduce((best, c) => (contrast(c, bg) > contrast(best, bg) ? c : best));

/** Mix two colours (t = 0 → a, 1 → b) as #rrggbb; returns `a` unchanged when either can't be parsed. */
export function mix(a: string, b: string, t: number): string {
  const x = parseColor(a);
  const y = parseColor(b);
  if (!x || !y) return a;
  return `#${x.map((v, i) => Math.round(v + (y[i]! - v) * t).toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Coastline colour: the `--coast` token when set; otherwise the land edge, pushed towards the ink colour until it
 * stands out from both sea and land (dark themes have very low land/sea contrast).
 */
export function coastColor(token: string, landEdge: string, sea: string, land: string, ink: string): string {
  if (token) return token;
  const s = parseColor(sea);
  if (!s || luminance(s) > 0.25) return landEdge; // light theme: the edge token already reads well
  let c = landEdge;
  for (let t = 0; t <= 0.8 && (contrast(c, sea) < 2.4 || contrast(c, land) < 1.6); t += 0.1) c = mix(landEdge, ink, t);
  return c;
}

/** Rings smaller than this (px) aren't drawn; below the label size they are drawn without a label. */
export const RING_MIN_PX = 6;
export const RING_LABEL_MIN_PX = 18;

/** Screen radius (px) of a ground circle of `km` at latitude `lat` on a Web Mercator world `wpx` px wide. */
export function ringRadiusPx(km: number, lat: number, wpx: number): number {
  const cos = Math.max(0.01, Math.cos((lat * Math.PI) / 180));
  return (km / (EARTH_KM * cos)) * wpx;
}
